import { useEffect, useRef } from "react";

/*
  useCall: encapsulates WebRTC call setup and signaling.
  - Accepts a WebSocket reference object (socketRef) instead of a raw socket so
    the hook doesn't overwrite unrelated socket handlers.
  - Handles both caller and callee flows based on isCaller flag.
  - Cleans up PeerConnection and tracks on unmount.
*/
export function useCall({
  socketRef,
  myMobile,
  recipientMobile,
  callType = "video",
  localVideoRef,
  remoteVideoRef,
  setStatus,
  isCaller = true,
  initialOffer = null,
  caller = null,
  isCallAccepted = true,
}) {
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const queuedCandidatesRef = useRef([]);

  useEffect(() => {
    if (!isCallAccepted) return; // Don't set up call if not accepted

    let mounted = true;
    const socket = socketRef?.current;
    if (!socket) {
      setStatus?.("No socket available");
      return;
    }

    const handleMessage = async (msg) => {
      try {
        const data = JSON.parse(msg.data);
        if (!peerRef.current) return;

        if (data.type === "call:offer") {
          setStatus?.("Receiving offer...");
          await peerRef.current.setRemoteDescription(data.offer);
          const answer = await peerRef.current.createAnswer();
          await peerRef.current.setLocalDescription(answer);
          socket.send(
            JSON.stringify({
              action: "signal",
              data: JSON.stringify({
                type: "call:answer",
                to: data.from,
                from: myMobile,
                answer,
              }),
            })
          );
          setStatus?.("Answering...");
        } else if (data.type === "call:answer") {
          await peerRef.current.setRemoteDescription(data.answer);
          // flush any queued ICE candidates that arrived before remoteDescription
          try {
            while (queuedCandidatesRef.current.length) {
              const c = queuedCandidatesRef.current.shift();
              try {
                await peerRef.current.addIceCandidate(c);
              } catch (e) {
                console.warn('Failed to add queued ICE candidate after answer:', e);
              }
            }
          } catch (e) {
            console.warn('Error flushing queued candidates after answer:', e);
          }
          setStatus?.("Connected");
        } else if (data.type === "call:candidate") {
          if (data.candidate) {
            // if remoteDescription not set yet, queue candidate
              try {
                if (!peerRef.current.remoteDescription || !peerRef.current.remoteDescription.type) {
                  queuedCandidatesRef.current.push(data.candidate);
                } else {
                  await peerRef.current.addIceCandidate(data.candidate);
                }
              } catch (e) {
                // if addIceCandidate still fails, queue as a fallback
                console.warn('addIceCandidate failed, queuing candidate:', e);
                try { queuedCandidatesRef.current.push(data.candidate); } catch (_) {}
              }
          }
        } else if (data.type === "call:cancel") {
          setStatus?.("Call cancelled");
        }
      } catch (err) {
        console.error("Error handling socket message in useCall:", err);
      }
    };

    const setupPeer = async () => {
      try {
        setStatus?.("Getting media...");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: callType === "video",
          audio: true,
        });
        if (!mounted) return;

        console.log("Call: Got local stream with tracks:", stream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled })));
        localStreamRef.current = stream;
        if (localVideoRef?.current) localVideoRef.current.srcObject = stream;

        // Use a STUN server so peers behind different NATs can discover reachable candidates.
        peerRef.current = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            // TODO: add TURN server here for better reliability in restrictive networks
          ],
        });
        stream.getTracks().forEach((track) => {
          peerRef.current.addTrack(track, stream);
        });

        peerRef.current.ontrack = (event) => {
          if (remoteVideoRef?.current) {
            console.log("Call: Received remote stream with tracks:", event.streams[0].getTracks().map(t => ({ kind: t.kind, enabled: t.enabled })));
            remoteVideoRef.current.srcObject = event.streams[0];
            // Ensure audio tracks are enabled and not muted
            event.streams[0].getAudioTracks().forEach(track => {
              track.enabled = true;
              console.log("Call: Audio track enabled:", track.enabled);
            });
            // Force play for audio elements
            if (remoteVideoRef.current.play && typeof remoteVideoRef.current.play === 'function') {
              remoteVideoRef.current.play().catch(e => console.warn("Call: Could not auto-play remote audio:", e));
            }
            setStatus?.("Connected");
          }
        };

        peerRef.current.onicecandidate = (event) => {
          if (event.candidate) {
            socket.send(
              JSON.stringify({
                action: "signal",
                data: JSON.stringify({
                  type: "call:candidate",
                  to: recipientMobile,
                  from: myMobile,
                  candidate: event.candidate,
                }),
              })
            );
          }
        };

        // listen for messages without clobbering socket.onmessage
        socket.addEventListener?.("message", handleMessage);

        if (isCaller) {
          setStatus?.("Creating offer...");
          const offer = await peerRef.current.createOffer();
          await peerRef.current.setLocalDescription(offer);
          socket.send(
            JSON.stringify({
              action: "signal",
              data: JSON.stringify({
                type: "call:offer",
                to: recipientMobile,
                from: myMobile,
                offer,
                callType,
              }),
            })
          );
          setStatus?.("Waiting for answer...");
        } else if (initialOffer) {
          // If we navigated with an initialOffer (caller already sent it), process it
          try {
            setStatus?.("Receiving offer...");
            await peerRef.current.setRemoteDescription(initialOffer);
            // flush any queued ICE candidates that arrived before remoteDescription
            try {
              while (queuedCandidatesRef.current.length) {
                const c = queuedCandidatesRef.current.shift();
                try {
                  await peerRef.current.addIceCandidate(c);
                } catch (e) {
                  console.warn('Failed to add queued ICE candidate after initialOffer:', e);
                }
              }
            } catch (e) {
              console.warn('Error flushing queued candidates after initialOffer:', e);
            }

            const answer = await peerRef.current.createAnswer();
            await peerRef.current.setLocalDescription(answer);
            socket.send(
              JSON.stringify({
                action: "signal",
                data: JSON.stringify({
                  type: "call:answer",
                  to: caller || recipientMobile,
                  from: myMobile,
                  answer,
                }),
              })
            );
            setStatus?.("Answering...");
          } catch (err) {
            console.error("Failed to process initial offer:", err);
          }
        }
      } catch (err) {
        console.error("Call setup failed:", err);
        setStatus?.("Call failed");
      }
    };

    setupPeer();

    return () => {
      mounted = false;
      try {
        socket.removeEventListener?.("message", handleMessage);
      } catch (e) {
        // ignore
      }
      try {
        if (peerRef.current) {
          peerRef.current.getSenders?.().forEach((s) => {
            try { s.track?.stop(); } catch (_) {}
          });
          peerRef.current.close?.();
        }
      } catch (e) {}
      try {
        localStreamRef.current?.getTracks?.().forEach((t) => t.stop());
      } catch (e) {}
    };
  }, [socketRef, myMobile, recipientMobile, callType, localVideoRef, remoteVideoRef, setStatus, isCaller, isCallAccepted]);

  return {
    mute: () => {
      localStreamRef.current?.getAudioTracks?.().forEach((track) => (track.enabled = false));
    },
    unmute: () => {
      localStreamRef.current?.getAudioTracks?.().forEach((track) => (track.enabled = true));
    },
    toggleCamera: () => {
      localStreamRef.current?.getVideoTracks?.().forEach((track) => (track.enabled = !track.enabled));
    },
    endCall: () => {
      const socket = socketRef?.current;
      socket?.send(
        JSON.stringify({
          action: "signal",
          data: JSON.stringify({
            type: "call:cancel",
            to: recipientMobile,
            from: myMobile,
          }),
        })
      );
    },
  };
}
